import { useMemo } from 'react';
import { History, ArrowRight, PlusCircle, Pencil } from 'lucide-react';
import type { ExpenseTransition } from '../../types/expense';
import { expenseFieldLabel } from '../../lib/api/mappers';
import { Spinner } from '../ui/Spinner';
import { formatDateTime } from '../../utils/trips';
import { formatearCOP } from '../../utils/expenses';
import { useExpenseCategories } from '../../hooks/useExpenseCategories';
import { useVehicles } from '../../hooks/useVehicles';
import { useDrivers } from '../../hooks/useDrivers';

// issue #112: mismo defecto y mismo arreglo que `LogbookTransitions` — este
// componente se abre desde tres páginas distintas (Gastos, y el expediente de
// viaje desde Viajes/Operador/Reportes), así que pide su propio catálogo con
// los mismos hooks en vez de perforar `categories`/`vehicles`/`drivers` a
// través de componentes que no los necesitan para nada más.
function resolverValor(
  field: string,
  valor: string,
  maps: { categorias: Map<string, string>; vehiculos: Map<string, string>; conductores: Map<string, string> },
): { texto: string; title?: string } {
  if (!valor) return { texto: '—' };
  if (field === 'amount' && /^\d+(\.\d{1,2})?$/.test(valor)) return { texto: formatearCOP(valor) };
  if (field === 'category_id' || field === 'categoryId') {
    return { texto: maps.categorias.get(valor) ?? valor.slice(0, 8), title: valor };
  }
  if (field === 'vehicle_id' || field === 'vehicleId') {
    return { texto: maps.vehiculos.get(valor) ?? valor.slice(0, 8), title: valor };
  }
  if (field === 'driver_id' || field === 'driverId') {
    return { texto: maps.conductores.get(valor) ?? valor.slice(0, 8), title: valor };
  }
  if (field === 'incurred_at' || field === 'incurredAt') {
    return { texto: formatDateTime(valor) };
  }
  if (field === 'trip_id' || field === 'tripId') {
    return { texto: valor.slice(0, 8), title: valor };
  }
  return { texto: valor };
}

/**
 * FE-12 — la novedad de un gasto: append-only, garantizada por la AUSENCIA del
 * privilegio `UPDATE` en la base. Ni el core puede reescribirla.
 *
 * Lo que hace que valga la pena pintarla es el "de qué a qué" de cada campo.
 * Una novedad que solo dijera *"alguien corrigió esto el martes"* no sirve
 * para lo único que se le va a pedir: saber qué importe decía antes un soporte
 * contable y quién lo cambió.
 *
 * ⚠️ Una asociación retirada llega con `to` vacío. Se pinta como "—" y no como
 * cadena vacía: un hueco en blanco se lee como un fallo de render, y este es un
 * dato que alguien decidió quitar.
 */
export function ExpenseTransitions({
  transitions,
  isLoading,
  error,
}: {
  transitions: ExpenseTransition[];
  isLoading: boolean;
  error: Error | null;
}) {
  const { categories } = useExpenseCategories();
  const { vehicles } = useVehicles();
  const { drivers } = useDrivers();

  const maps = useMemo(() => ({
    categorias: new Map(categories.map(c => [c.id, c.label])),
    vehiculos: new Map(vehicles.map(v => [v.id, v.plate])),
    conductores: new Map(drivers.map(d => [d.id, d.name])),
  }), [categories, vehicles, drivers]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
        <History size={15} style={{ color: 'var(--accent)' }} />
        Novedad del gasto
      </h4>

      {isLoading && (
        <div style={{ padding: '1rem', display: 'flex', justifyContent: 'center' }}><Spinner /></div>
      )}

      {!isLoading && error && (
        <p style={{ margin: 0, fontSize: '12px', color: '#EF4444' }}>
          No se pudo cargar la novedad: {error.message}
        </p>
      )}

      {!isLoading && !error && transitions.length === 0 && (
        <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>Sin anotaciones.</p>
      )}

      {!isLoading && !error && transitions.length > 0 && (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {transitions.map((t, i) => {
            const esCorreccion = t.action === 'CORRECTED';
            const color = esCorreccion ? '#F59E0B' : '#10B981';
            return (
              <li
                key={`${t.recordedAt}-${i}`}
                style={{
                  background: 'var(--bg-raised)',
                  border: '1px solid var(--border)',
                  borderLeft: `3px solid ${color}`,
                  borderRadius: 'var(--radius-md)',
                  padding: '0.625rem 0.875rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.375rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '12px', fontWeight: 800, color }}>
                  {esCorreccion ? <Pencil size={12} /> : <PlusCircle size={12} />}
                  {esCorreccion ? 'Corrección' : 'Registro inicial'}
                </div>

                {t.changes.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {t.changes.map(c => {
                      const desde = resolverValor(c.field, c.from, maps);
                      const hasta = resolverValor(c.field, c.to, maps);
                      return (
                        <div key={c.field} style={{ fontSize: '11.5px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'baseline', gap: '0.375rem', flexWrap: 'wrap' }}>
                          <strong style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{expenseFieldLabel(c.field)}:</strong>
                          <span style={{ textDecoration: 'line-through', opacity: 0.75 }} title={desde.title}>{desde.texto}</span>
                          <ArrowRight size={11} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                          <span style={{ fontWeight: 700, color: 'var(--text-primary)' }} title={hasta.title}>{hasta.texto}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {t.reason && (
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>“{t.reason}”</p>
                )}

                {/* Mismo límite que la novedad del viaje y los cambios de una novedad: el
                    contrato entrega el `actor` como userId crudo y no hay
                    endpoint que lo resuelva a nombre sin permisos de admin
                    (PENDIENTES.md). */}
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }} title={t.actor}>
                  {formatDateTime(t.recordedAt)} · {t.actor === 'system' ? 'automático' : `usuario ${t.actor.slice(0, 8)}`}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
